import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Stack, TextField, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LockIcon from '@mui/icons-material/LockOutlined';
import { toast } from 'react-toastify';

import { AppContainer, MainContentContainer, BodyContainer } from '../../resources/styling/appStyle';
import { SideMenu } from '../../components/layouts/SideMenu';
import { Header } from '../../components/layouts/Header';
import { useIdentityAuth } from '../../auth';
import {
  sa, Card, DataTable, PillButton, SectionHeaderRow, SelectPill,
} from '../../components/superAdminUi';
import SuperAdminHistory from '../../components/superAdminUi/SuperAdminHistory';
import {
  listSuperAdmins, addSuperAdmin, removeSuperAdmin, promoteSuperAdmin,
  demoteSuperAdmin, resetSuperAdminPassword, resetSuperAdminMfa,
} from '../../services/api/superAdminManagementService';

// The backend enforces these; the UI only mirrors them for affordance.
const PROTECTED_EMAILS = [
  'timhandley+superadmin@welbee.co.uk',
  'marksolomons+superadmin@welbee.co.uk',
];
const MANAGER_EMAILS = PROTECTED_EMAILS; // Tim & Mark may promote/demote.

const TIER_OPTIONS = [
  { value: 'all', label: 'All tiers' },
  { value: 'super-user', label: 'Super Users' },
  { value: 'super-admin', label: 'Standard' },
];

const lower = (s) => (s || '').toLowerCase();
const norm = (s) => (s || '').replace(/\s+/g, '').toLowerCase();
// The search API returns display names under roleNames ("Super User"); tolerate codes too.
const rolesOf = (u) =>
  (u.roleNames || u.roles || u.roleCodes || []).map((r) => (typeof r === 'string' ? r : r.code || r.name || ''));

const toRow = (u) => {
  const email = u.email || u.emailAddress || u.email_Value || '';
  const names = rolesOf(u);
  return {
    id: u.userId || u.id,
    email,
    name: `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.name || email,
    isSuperUser: names.some((n) => norm(n) === 'superuser'),
    status: u.status || '',
    isProtected: PROTECTED_EMAILS.map(lower).includes(lower(email)),
  };
};

// Super Admin surface font wrapper, matching User Management's section shell.
const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  font-family: ${sa.font.family};
`;

// Bordered, leading-icon search field — the same affordance as User Management.
const SearchBox = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  svg {
    position: absolute;
    left: 12px;
    font-size: 18px;
    color: ${sa.color.textGray};
    pointer-events: none;
  }
  input {
    width: 240px;
    max-width: 100%;
    border: 1px solid ${sa.color.stroke};
    border-radius: ${sa.radius.sm};
    background: ${sa.color.white};
    font-family: ${sa.font.family};
    font-size: 13px;
    color: ${sa.color.text};
    padding: 9px 12px 9px 38px;

    &::placeholder {
      color: ${sa.color.textGray};
    }
    &:focus {
      outline: none;
      border-color: ${sa.color.teal};
    }
  }
`;

const NameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const UserName = styled.span`
  font-weight: ${sa.font.semibold};
  color: ${sa.color.text};
  overflow: hidden;
  text-overflow: ellipsis;
`;
const UserEmail = styled.span`
  font-size: 12px;
  color: ${sa.color.textGray};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BadgeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;
const TierBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: ${sa.radius.sm};
  font-weight: ${sa.font.bold};
  font-size: 11px;
  line-height: 1;
  background: ${(p) => (p.$super ? sa.color.schoolBg : sa.color.mist)};
  color: ${(p) => (p.$super ? sa.color.blue : sa.color.textGray)};
`;
const ProtectedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: ${sa.radius.sm};
  font-weight: ${sa.font.bold};
  font-size: 11px;
  line-height: 1;
  background: ${sa.color.groupBg};
  color: ${sa.color.pink};

  svg {
    font-size: 12px;
  }
`;
const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: ${sa.radius.sm};
  font-weight: ${sa.font.bold};
  font-size: 11px;
  line-height: 1;
  background: ${(p) => (p.$active ? '#e6f4ea' : sa.color.mist)};
  color: ${(p) => (p.$active ? '#1b7a43' : sa.color.textGray)};
`;

const ActionCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
`;

export default function SuperManagement() {
  const { user, loading: authLoading, hasRole } = useIdentityAuth();
  const isManager = useMemo(
    () => MANAGER_EMAILS.map(lower).includes(lower(user?.email)),
    [user],
  );

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [tier, setTier] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ email: '', firstName: '', lastName: '', phoneNumber: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const users = await listSuperAdmins();
      setRows(users.map(toRow).filter((r) => r.id).sort((a, b) => Number(b.isSuperUser) - Number(a.isSuperUser)));
    } catch (e) {
      toast.error(e.message || 'Failed to load super admins');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && hasRole('SuperUser')) load();
  }, [authLoading, hasRole, load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      const matchesTier = tier === 'all' || (tier === 'super-user' ? r.isSuperUser : !r.isSuperUser);
      const matchesQuery = !q || r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q);
      return matchesTier && matchesQuery;
    });
  }, [rows, query, tier]);

  const run = async (id, fn, success) => {
    setBusyId(id);
    try {
      await fn();
      toast.success(success);
      await load();
    } catch (e) {
      toast.error(e.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  };

  const onRemove = (row) => {
    if (window.confirm(`Remove ${row.email}? This deletes the account.`)) {
      run(row.id, () => removeSuperAdmin(row.id, 'Removed via Super Management'), 'Super admin removed');
    }
  };

  const submitAdd = async () => {
    setSaving(true);
    try {
      await addSuperAdmin(form);
      toast.success('Super admin invited');
      setAddOpen(false);
      setForm({ email: '', firstName: '', lastName: '', phoneNumber: '' });
      await load();
    } catch (e) {
      toast.error(e.message || 'Failed to add super admin');
    } finally {
      setSaving(false);
    }
  };

  const page = (content) => (
    <AppContainer>
      <SideMenu />
      <BodyContainer>
        <Header />
        <MainContentContainer>{content}</MainContentContainer>
      </BodyContainer>
    </AppContainer>
  );

  if (!authLoading && !hasRole('SuperUser')) {
    return page(
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Card>
          <SectionHeaderRow title='Super Management' size='lg' />
          <p style={{ marginTop: 12, color: sa.color.textGray, fontFamily: sa.font.family, fontSize: 14 }}>
            You need the Super User role to access this page.
          </p>
        </Card>
      </Box>,
    );
  }

  const columns = [
    {
      key: 'name',
      label: 'User',
      width: '32%',
      render: (row) => (
        <NameCell>
          <UserName>{row.name}</UserName>
          <UserEmail>{row.email}</UserEmail>
        </NameCell>
      ),
    },
    {
      key: 'tier',
      label: 'Tier',
      width: '20%',
      render: (row) => (
        <BadgeRow>
          <TierBadge $super={row.isSuperUser}>{row.isSuperUser ? 'Super User' : 'Super Admin'}</TierBadge>
          {row.isProtected && (
            <Tooltip title='Protected — cannot be removed or demoted'>
              <ProtectedBadge>
                <LockIcon />
                Protected
              </ProtectedBadge>
            </Tooltip>
          )}
        </BadgeRow>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: '14%',
      render: (row) => <StatusBadge $active={row.status === 'Active'}>{row.status || '—'}</StatusBadge>,
    },
    {
      key: 'action',
      label: 'Actions',
      align: 'right',
      render: (row) =>
        busyId === row.id ? (
          <ActionCell>
            <CircularProgress size={18} sx={{ color: sa.color.teal }} />
          </ActionCell>
        ) : (
          <ActionCell>
            <PillButton
              type='button'
              $variant='outline'
              onClick={() => setExpandedId((current) => (current === row.id ? null : row.id))}
            >
              {expandedId === row.id ? 'Hide History' : 'Reveal History'}
            </PillButton>
            <PillButton
              type='button'
              $variant='outline'
              onClick={() => run(row.id, () => resetSuperAdminPassword(row.id), 'Set-password link sent')}
            >
              Reset Password
            </PillButton>
            <PillButton
              type='button'
              $variant='outline'
              onClick={() => run(row.id, () => resetSuperAdminMfa(row.id), 'MFA reset')}
            >
              Reset MFA
            </PillButton>
            {isManager && !row.isSuperUser && (
              <PillButton
                type='button'
                onClick={() => run(row.id, () => promoteSuperAdmin(row.id), 'Promoted to Super User')}
              >
                Promote
              </PillButton>
            )}
            {isManager && row.isSuperUser && (
              <Tooltip title={row.isProtected ? 'Protected — cannot demote' : ''}>
                <span>
                  <PillButton
                    type='button'
                    $variant='outline'
                    disabled={row.isProtected}
                    onClick={() => run(row.id, () => demoteSuperAdmin(row.id), 'Demoted to standard super admin')}
                  >
                    Demote
                  </PillButton>
                </span>
              </Tooltip>
            )}
            <Tooltip title={row.isProtected ? 'Protected — cannot remove' : ''}>
              <span>
                <PillButton type='button' $variant='danger' disabled={row.isProtected} onClick={() => onRemove(row)}>
                  Remove
                </PillButton>
              </span>
            </Tooltip>
          </ActionCell>
        ),
    },
  ];

  const emptyNode = loading ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, color: sa.color.textGray }}>
      <CircularProgress size={18} sx={{ color: sa.color.teal }} />
      Loading super admins…
    </span>
  ) : rows.length === 0 ? (
    'No super admins found'
  ) : (
    'No matches'
  );

  return page(
    <Box>
      <Section>
        <SectionHeaderRow title='Super Management' subtitle='Manage super admin access' size='lg'>
          <SearchBox>
            <SearchIcon />
            <input
              type='text'
              placeholder='Search name or email'
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label='Search super admins'
            />
          </SearchBox>
          <SelectPill value={tier} onChange={setTier} options={TIER_OPTIONS} ariaLabel='Tier' testId='tier-dropdown' />
          <PillButton type='button' $variant='danger' $size='lg' onClick={() => setAddOpen(true)}>
            Add super admin
          </PillButton>
        </SectionHeaderRow>

        <DataTable
          columns={columns}
          rows={loading ? [] : filtered}
          getRowKey={(row) => row.id}
          empty={emptyNode}
          isRowExpanded={(row) => row.id === expandedId}
          renderExpanded={(row) => (
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <SuperAdminHistory userId={row.id} />
            </Box>
          )}
        />

        <p style={{ margin: 0, fontSize: 12, color: sa.color.textGray, fontFamily: sa.font.family }}>
          Protected accounts (Tim &amp; Mark) can&apos;t be removed or demoted · promote / demote available to Tim &amp;
          Mark only · every action is audited.
        </p>
      </Section>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add super admin</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} fullWidth required />
            <TextField label="First name" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} fullWidth required />
            <TextField label="Last name" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} fullWidth required />
            <TextField label="Phone (optional)" value={form.phoneNumber} onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={submitAdd} disabled={saving || !form.email || !form.firstName || !form.lastName}>
            {saving ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>,
  );
}
